import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { keepingNavState } from '../utils/searchParamsNavigation';
import axiosInstance from '../api';

import AddIcon from '@mui/icons-material/Add';
import SettingsIcon from '@mui/icons-material/Settings';

import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import EntityDetailDrawer from './EntityDetailDrawer';
import { toTitleCase, UNCATEGORIZED } from '../utils/textFormat';
import { BenchmarkRun, benchmarkService } from '../services/benchmarkService';
import { baseFlowCode, benchmarkFlowCode, flowFamily } from './Benchmark/benchmarkFlowCodes';
import { Project } from '../services/projectService';

import Sidebar from './Sidebar';
import AppHeader from './AppHeader';
import WorkspaceSelector from './WorkspaceSelector';
import ProjectAttentionStrip, {
    OPERATIONS_BUCKETS, ATTENTION_WINDOW, AttentionBucket, RunAttention
} from './ProjectAttentionStrip';
import ProjectWorkflowCard, { Campaign, describeRun, describeRunDetail } from './ProjectWorkflowCard';
import ChatAssistance, { AgentReference, ChatShowRail, useChatPanel } from './ChatAssistance';
import InsightGroupDrawer, { OpenAgentInsight, OpenInsightGroup } from './Insights/InsightGroupDrawer';
import ProjectBusinessView, { useProjectBusinessData } from './ProjectOutcomes/ProjectBusinessView';
import DecisionListDrawer from './ProjectOutcomes/DecisionListDrawer';
import { GateActions } from '../services/caseWorkflowService';
import { campaignRanks } from './ProjectOutcomes/pipelineOrder';
import { CatalogColumns, CatalogGroup, useCollapsedGroups } from './CatalogGroups';
import { reasoningPathFor } from './Insights/reasoningLink';
import WorkflowRunPanel, { InitialRuns } from './ExecutionStats/WorkflowRunPanel';
import { KeySummary, useWorkflowRunStats } from './ExecutionStats/useExecutionSummary';
import { attentionInk } from './ExecutionStats/executionStatsVocabulary';
import DrawerAboutSection from './DrawerAboutSection';
import { dsVar } from '../theme/tokens';

/**
 * The page's two tabs: the project's business state, and its workflow catalog.
 * Kept in the query string (`?tab=workflows`) so a link can open either.
 */
type ProjectTab = 'status' | 'workflows';
const PROJECT_TABS: { key: ProjectTab; label: string }[] = [
    { key: 'status', label: 'Overview' },
    { key: 'workflows', label: 'Workflows' },
];

/**
 * The tab last chosen, for the browser session.
 *
 * Every Back that returns here — the campaign page, the flow editor, the backtest
 * dashboard — names the bare project path, so without this a reader who left
 * from a workflow card would come back to the Status tab.
 */
const LAST_TAB_KEY = 'project_page_tab';

const readLastTab = (): ProjectTab => {
    try {
        return sessionStorage.getItem(LAST_TAB_KEY) === 'workflows' ? 'workflows' : 'status';
    } catch {
        return 'status';
    }
};

const writeLastTab = (tab: ProjectTab) => {
    try {
        sessionStorage.setItem(LAST_TAB_KEY, tab);
    } catch {
        // Private windows and blocked storage: the tab still switches for this visit.
    }
};

/** The tab the query string names, or the one last chosen. */
const projectTabFromParams = (params: URLSearchParams): ProjectTab => {
    const requested = params.get('tab');
    return requested === 'status' || requested === 'workflows' ? requested : readLastTab();
};

/**
 * Two roomy columns beside the docked chat on a 1440px screen (about 736px of
 * content), where the marketplace's 26rem would drop to one stretched column.
 */
const CATALOG_MIN_COLUMN_WIDTH = '21rem';

/**
 * Reports one workflow's run statistics to the page. Renders nothing.
 *
 * Mounted for every workflow whichever tab is showing, so the map's broken-run
 * marks and the attention roll-up do not wait for the catalog's cards. The
 * cards read the same summary; the hook batches and caches, so it is one request.
 */
const RunStatsReporter: React.FC<{
    campaign: Campaign;
    onRunStats: (campaignId: string, summary: KeySummary | null) => void;
}> = ({ campaign, onRunStats }) => {
    const stats = useWorkflowRunStats({ campaignId: campaign.id, flowCode: campaign.flow_template_code });

    useEffect(() => {
        // Between the campaign read and the flow fallback the hook briefly has
        // neither data nor an error; only a settled answer is reported.
        if (stats.loading) return;
        if (stats.data) onRunStats(campaign.id, stats.data);
        else if (stats.error) onRunStats(campaign.id, null);
    }, [campaign.id, stats.loading, stats.data, stats.error, onRunStats]);

    return null;
};

const ProjectReports = () => {
    const { projectId } = useParams<{ projectId: string }>();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams, setSearchParams] = useSearchParams();
    const activeTab = projectTabFromParams(searchParams);
    const selectTab = (tab: ProjectTab) => {
        if (tab === activeTab) return;
        writeLastTab(tab);
        const next = new URLSearchParams(searchParams);
        next.set('tab', tab);
        setSearchParams(next, keepingNavState(location.state, { replace: true }));
    };

    const [project, setProject] = useState<Project | null>(null);
    const [campaigns, setCampaigns] = useState<Campaign[]>([]);

    const [projectLoading, setProjectLoading] = useState(true);
    const [campaignsLoading, setCampaignsLoading] = useState(true);
    const [detailCampaign, setDetailCampaign] = useState<Campaign | null>(null);
    /** The runs the drawer opens on; unset opens it on the totals. */
    const [detailRuns, setDetailRuns] = useState<InitialRuns | undefined>(undefined);
    /** The insight an assistant reference opened; never open alongside the workflow drawer. */
    const [openInsight, setOpenInsight] = useState<OpenAgentInsight | null>(null);
    /** The review gate whose waiting cases are listed. */
    const [openGate, setOpenGate] = useState<GateActions | null>(null);
    /** The card a waiting decision is made on, with the campaign that produced it. */
    const [reviewCard, setReviewCard] = useState<{ campaignId: string; group: OpenInsightGroup } | null>(null);
    /** Bumped after a review is submitted, so the business view re-reads. */
    const [businessRefresh, setBusinessRefresh] = useState(0);
    /**
     * The Status tab's data, read here whichever tab is showing: its pipeline
     * orders the catalog so it reads in the map's order, and its gates count the
     * decisions waiting on the tab bar.
     */
    const business = useProjectBusinessData(projectId, businessRefresh);
    const { isCollapsed, toggle: toggleGroup } = useCollapsedGroups();
    const drawerOpen = Boolean(detailCampaign || openInsight || openGate || reviewCard);
    /**
     * Every drawer docks on a wide screen, where two open at once would sit side
     * by side and squeeze the content to nothing; each opener closes the rest
     * first.
     */
    const closeDrawers = () => {
        setDetailCampaign(null);
        setOpenInsight(null);
        setOpenGate(null);
        setReviewCard(null);
    };
    // Hideable here: the pipeline map and the catalog want the width, and a reader
    // who is not asking questions has no use for the column.
    const chatPanel = useChatPanel(drawerOpen, false, { hideKey: 'project' });
    /**
     * The latest backtest per workflow, keyed by the workflow's own flow code.
     *
     * Runs are recorded under the BENCHMARK_ prefixed code, so the key is
     * stripped back to match what a campaign carries.
     */
    const [runsByFlowCode, setRunsByFlowCode] = useState<Map<string, BenchmarkRun>>(new Map());
    /**
     * Each workflow's run statistics as its RunStatsReporter reports them: the
     * summary, or null if it could not be loaded. A workflow absent from the map
     * has not reported yet.
     */
    const [runStatsById, setRunStatsById] = useState<Record<string, KeySummary | null>>({});
    /** Narrows the catalog to the workflows behind one attention chip. */
    const [attentionFilter, setAttentionFilter] = useState<AttentionBucket | null>(null);

    useEffect(() => {
        const fetchProjectDetails = async () => {
            try {
                const orgId = localStorage.getItem('org_id');
                if (!orgId || !projectId) {
                    setProjectLoading(false);
                    return;
                }
                const response = await axiosInstance.get(
                    `/projects/${projectId}?orgId=${orgId}`
                );
                if (response.data && response.data.project) {
                    setProject(response.data.project);
                }
            } catch (error) {
                console.error("Failed to fetch project:", error);
            } finally {
                setProjectLoading(false);
            }
        };

        const fetchCampaigns = async () => {
            try {
                const orgId = localStorage.getItem('org_id');
                if (!orgId || !projectId) {
                    setCampaignsLoading(false);
                    return;
                }
                const response = await axiosInstance.get(
                    `/campaigns?orgId=${orgId}&projectId=${projectId}&page=1&limit=50`
                );
                if (response.data && response.data.campaigns) {
                    setCampaigns(response.data.campaigns);
                }
            } catch (error) {
                console.error("Failed to fetch campaigns:", error);
            } finally {
                setCampaignsLoading(false);
            }
        };

        /**
         * Which of this project's workflows have been backtested.
         *
         * One request for the whole page. A failure here costs the cards their
         * backtest line and nothing else, so it is logged rather than surfaced —
         * the catalog is the page's actual content.
         */
        const fetchBenchmarkRuns = async () => {
            if (!projectId) return;
            try {
                const runs = await benchmarkService.getProjectCurrentRuns(projectId);
                // Keyed by family so a published build's stamped code still finds
                // its card. A supplied-sample run scores one user's sample, not
                // the workflow, so it never stands as the card's backtest.
                setRunsByFlowCode(new Map(
                    runs
                        .filter(run => run.dataSource !== 'user_supplied')
                        .map(run => [flowFamily(baseFlowCode(run.flowTemplateCode)), run])
                ));
            } catch (error) {
                console.error("Failed to fetch benchmark runs:", error);
            }
        };

        fetchProjectDetails();
        fetchCampaigns();
        fetchBenchmarkRuns();
    }, [projectId]);

    /**
     * Campaigns grouped by the campaign_type_name the campaigns API denormalizes
     * from the flow template, falling back to a title-cased campaign_type and then
     * "Uncategorized".
     *
     * Groups, and the cards within them, follow the pipeline map left to right: a
     * group sits at its earliest workflow's column. Groups with no workflow on the
     * map follow, alphabetical, with "Uncategorized" pinned last as in the
     * operators marketplace.
     */
    const ranks = useMemo(() => campaignRanks(business.pipeline), [business.pipeline]);
    const groupedCampaigns = useMemo(() => {
        const groups = new Map<string, Campaign[]>();
        campaigns.forEach(campaign => {
            // campaign_type_name is already a display label; only the code needs title-casing.
            const label = campaign.campaign_type_name?.trim() || toTitleCase(campaign.campaign_type);
            const existing = groups.get(label);
            if (existing) {
                existing.push(campaign);
            } else {
                groups.set(label, [campaign]);
            }
        });
        const rankOf = (campaign: Campaign) => ranks.get(campaign.id) ?? Number.POSITIVE_INFINITY;
        const firstRank = (group: Campaign[]) => Math.min(...group.map(rankOf));
        groups.forEach((group) => group.sort((a, b) =>
            rankOf(a) - rankOf(b) || a.campaign_name.localeCompare(b.campaign_name)));
        return Array.from(groups.entries()).sort(([a, groupA], [b, groupB]) => {
            if (a === UNCATEGORIZED) return 1;
            if (b === UNCATEGORIZED) return -1;
            const byRank = firstRank(groupA) - firstRank(groupB);
            // Infinity - Infinity is NaN: two unranked groups fall through to the name.
            return (Number.isNaN(byRank) ? 0 : byRank) || a.localeCompare(b);
        });
    }, [campaigns, ranks]);

    const handleRunStats = useCallback((campaignId: string, summary: KeySummary | null) => {
        setRunStatsById(prev => (prev[campaignId] === summary ? prev : { ...prev, [campaignId]: summary }));
    }, []);

    /**
     * The workflows with something in each attention bucket, and the strip's
     * roll-up of them. The roll-up is null until every card has reported, so
     * the strip never says "nothing needs attention" about numbers still on
     * their way.
     */
    const { attentionIds, runAttention } = useMemo(() => {
        const ids: Record<AttentionBucket, Set<string>> = {
            failed: new Set(), stalled: new Set(), pendingReview: new Set()
        };
        let unavailable = 0;
        let pending = 0;
        let staleAfterHours: number | null = null;
        campaigns.forEach(campaign => {
            if (!(campaign.id in runStatsById)) {
                pending += 1;
                return;
            }
            const summary = runStatsById[campaign.id];
            if (!summary) {
                unavailable += 1;
                return;
            }
            staleAfterHours = summary.staleAfterHours;
            // No figures for the window means no runs in it.
            const counts = summary.counts[ATTENTION_WINDOW];
            if (!counts) return;
            // A null bucket is untracked, not zero; either way it flags nothing.
            OPERATIONS_BUCKETS.forEach(bucket => {
                if ((counts[bucket] ?? 0) > 0) ids[bucket].add(campaign.id);
            });
        });
        const rollUp: RunAttention | null = campaignsLoading || pending > 0
            ? null
            : {
                workflows: {
                    failed: ids.failed.size,
                    stalled: ids.stalled.size,
                    pendingReview: ids.pendingReview.size
                },
                unavailable,
                staleAfterHours
            };
        return { attentionIds: ids, runAttention: rollUp };
    }, [campaigns, campaignsLoading, runStatsById]);

    /**
     * What each tab carries, so the hidden tab still says whether it needs a
     * look: the cases waiting on a decision, and the workflow count with a mark
     * when any workflow's runs failed or stalled. Null until known.
     */
    const casesWaiting = business.actions
        ? business.actions.gates.reduce((sum, gate) => sum + gate.openCases, 0)
        : null;
    const brokenWorkflows = runAttention
        ? new Set([...attentionIds.failed, ...attentionIds.stalled]).size
        : 0;
    const tabBadges: Record<ProjectTab, { count: number | null; title?: string; flagged?: boolean }> = {
        status: {
            count: casesWaiting || null,
            title: casesWaiting ? `${casesWaiting.toLocaleString()} ${casesWaiting === 1 ? 'case' : 'cases'} waiting on a decision` : undefined,
        },
        workflows: {
            count: campaignsLoading ? null : campaigns.length,
            title: brokenWorkflows ? `${brokenWorkflows} ${brokenWorkflows === 1 ? 'workflow has' : 'workflows have'} failed or stalled runs` : undefined,
            flagged: brokenWorkflows > 0,
        },
    };

    const visibleGroups = useMemo(() => {
        if (!attentionFilter) return groupedCampaigns;
        const keep = attentionIds[attentionFilter];
        return groupedCampaigns
            .map(([label, group]) => [label, group.filter(campaign => keep.has(campaign.id))] as [string, Campaign[]])
            .filter(([, group]) => group.length > 0);
    }, [groupedCampaigns, attentionFilter, attentionIds]);

    /**
     * Where Back returns to from anything this page opens.
     *
     * The label is for pages that can be reached from more than one place —
     * the backtest dashboard is opened both from here and from the flow
     * editor, and its Back button has to say which one it means.
     */
    const returnToProject = {
        pathname: `/analytics/projects/${projectId}`,
        label: 'Back to project'
    };

    /**
     * The flow this campaign addresses.
     *
     * The campaigns API denormalizes it, so the common path costs nothing. The
     * operator lookup remains as a fallback for a campaign the API could not
     * resolve a code for — it used to run on every click.
     */
    const resolveFlowTemplateCode = async (campaign: Campaign): Promise<string | undefined> => {
        if (campaign.flow_template_code) return campaign.flow_template_code;
        const response = await axiosInstance.get(`/operators/${campaign.operator_code}`);
        return response.data?.operator?.flow_template_code ?? response.data?.flow_template_code;
    };

    const handleCustomize = async (campaign: Campaign) => {
        try {
            const flowTemplateCode = await resolveFlowTemplateCode(campaign);
            navigate('/AIStudio/customize', {
                state: {
                    flow_template_code: flowTemplateCode,
                    operator_id: campaign.id,
                    operator_name: campaign.campaign_name,
                    fromOperator: true,
                    // Carried so the editor's Run button can execute the flow
                    // against this campaign and link back to its results.
                    campaign_id: campaign.id,
                    project_id: projectId,
                    // Where Back returns to. Both ids above are carried for the
                    // Run button, so the editor cannot tell the project page
                    // from the campaign page without being told.
                    returnTo: returnToProject
                }
            });
        } catch (error) {
            console.error("Failed to fetch operator details:", error);
        }
    };

    /** The campaign's latest backtest, or undefined if it has never been run. */
    const runFor = (campaign: Campaign): BenchmarkRun | undefined =>
        // Normalized on both sides: a workflow that is itself a benchmark flow
        // carries the prefixed code, and the map is keyed unprefixed.
        campaign.flow_template_code
            ? runsByFlowCode.get(flowFamily(baseFlowCode(campaign.flow_template_code)))
            : undefined;

    /**
     * Opens the backtest dashboard on this workflow's latest run.
     *
     * returnTo points straight back here, so the dashboard's Back returns to
     * this page rather than to the flow editor — the reader arrived from a card,
     * not from the editor, and has no reason to land there.
     *
     * The rest of the state mirrors what the editor sends, so the dashboard has
     * the same context either way.
     */
    const handleViewBacktest = (campaign: Campaign, run: BenchmarkRun) => {
        navigate(`/benchmark-dashboard/${run.runKey}`, {
            state: {
                run_key: run.runKey,
                run_id: run.runId,
                flow_template_code: benchmarkFlowCode(campaign.flow_template_code ?? ''),
                base_flow_template_code: campaign.flow_template_code,
                project_id: projectId,
                operator_name: campaign.campaign_name,
                operator_id: campaign.id,
                campaign_id: campaign.id,
                fromOperator: true,
                returnTo: returnToProject
            }
        });
    };

    const detailDrawerRun = detailCampaign ? runFor(detailCampaign) : undefined;

    return (
        <div className="flex h-screen overflow-hidden bg-page font-sans text-fg selection:bg-accent selection:text-on-accent">
            <Sidebar activePage="hub" forceCollapsed={drawerOpen} />

            {/* Main Content */}
            <main className="flex-1 flex flex-col overflow-hidden bg-page">
                <AppHeader left={<WorkspaceSelector currentProjectId={projectId} />} />

                {/*
                  * Assistant and content side by side, both bounded by the
                  * viewport: the chat column keeps its own scroll on the left
                  * while the content scrolls beside it, the way the campaign
                  * page splits its Insights tab. A drawer docks on the far
                  * right, so it never comes between the two.
                  */}
                <div className="flex-1 flex overflow-hidden">
                    {/*
                      * Project-scoped: no campaign is selected here, so no
                      * campaignId goes to the agent.
                      */}
                    <div className={`flex-none flex flex-col h-full ${chatPanel.hidden ? '' : 'border-r border-line'}`} style={chatPanel.style}>
                        <ChatAssistance
                            key={chatPanel.interactionId}
                            {...chatPanel.chatProps}
                            title="Investigation Assistant"
                            initialMessage="Hello! I'm your Data[S]cient assistant. Ask me about this project — its workflows, what they have found, or where to look next."
                            contextData={{ projectId }}
                            onOpenReference={(ref: AgentReference) => {
                                closeDrawers();
                                setOpenInsight({ kind: 'insight', ...ref });
                            }}
                            activeReferenceId={openInsight?.insightId ?? null}
                        />
                    </div>
                    {chatPanel.hidden && <ChatShowRail title="Investigation Assistant" onShow={chatPanel.show} side="left" />}

                    {/*
                      * The project description sits inside the title block rather
                      * than as a sibling below it, and the tabs share its row, so
                      * each tab's content starts near the top. min-w-0 lets the
                      * card grid shrink beside the assistant instead of pushing it
                      * off-screen.
                      */}
                    <div className="flex-1 min-w-0 overflow-y-auto p-6 lg:p-8 space-y-5 text-left">
                        {/* Title and tabs: one row, or the tabs on a row of their own once the column narrows. */}
                        <div className="flex flex-wrap items-center justify-between gap-x-6 gap-y-3">
                            <div className="min-w-0 flex-1 basis-64">
                                <h2 className="font-mono text-2xl m-0 p-0 text-left" style={{ color: dsVar('accent-fg') }}>
                                    {projectLoading ? 'Loading Project...' : (project?.name || project?.projectName || 'Project Details')}
                                </h2>
                                {!projectLoading && project && (
                                    <p className="text-fg-2 text-sm m-0 mt-1 max-w-3xl line-clamp-2">
                                        {project.description || project.projectDescription}
                                    </p>
                                )}
                            </div>
                            {/* The same pill toggle as a workflow's Insights / Run health / Explainability. */}
                            <div role="tablist" aria-label="Project views" className="shrink-0 bg-surface p-1 rounded-full border border-line flex gap-1">
                                {PROJECT_TABS.map(tab => {
                                    const badge = tabBadges[tab.key];
                                    return (
                                        <button
                                            key={tab.key}
                                            role="tab"
                                            aria-selected={activeTab === tab.key}
                                            onClick={() => selectTab(tab.key)}
                                            title={badge.title}
                                            className={`px-8 py-2 rounded-full text-xs font-bold uppercase tracking-wider whitespace-nowrap transition-all border-none cursor-pointer flex items-center gap-2 ${activeTab === tab.key
                                                ? 'bg-accent text-on-accent'
                                                : 'text-fg-2 hover:text-fg-body bg-transparent'
                                                }`}
                                        >
                                            {tab.label}
                                            {badge.count !== null && <span className="opacity-60 m-0">{badge.count}</span>}
                                            {badge.flagged && (
                                                <span
                                                    role="img"
                                                    aria-label={badge.title}
                                                    className="m-0 w-2 h-2 rounded-full"
                                                    style={{ backgroundColor: attentionInk('failed') }}
                                                />
                                            )}
                                        </button>
                                    );
                                })}
                            </div>
                        </div>

                        {/*
                          * Status: decisions waiting on a person at each review
                          * gate, work ready for the next workflow, and what the
                          * decisions decided. A run that succeeded says nothing
                          * about whether its case is waiting on a person.
                          */}
                        {activeTab === 'status' && projectId && (
                            <ProjectBusinessView
                                data={business}
                                runStatsById={runStatsById}
                                onOpenGate={(gate) => {
                                    closeDrawers();
                                    setOpenGate(gate);
                                }}
                                onOpenWorkflow={(campaignId) => {
                                    const campaign = campaigns.find((c) => c.id === campaignId);
                                    if (!campaign) return;
                                    closeDrawers();
                                    setDetailRuns(undefined);
                                    setDetailCampaign(campaign);
                                }}
                                onOpenCaseOperations={() => navigate(`/analytics/projects/${projectId}/case-workflow?window=all`)}
                            />
                        )}

                        {activeTab === 'workflows' && (
                            <section className="text-left p-0 m-0">
                                <div className="flex items-center justify-between gap-4 mb-4">
                                    {/*
                                      * Workflows whose runs failed or stalled sit on the
                                      * heading as chips that filter the catalog, and only
                                      * when there are any.
                                      */}
                                    <div className="flex flex-wrap items-center gap-x-3 gap-y-2 min-w-0">
                                        <h3 className="text-lg font-medium text-fg m-0">Your Workflows</h3>
                                        <ProjectAttentionStrip
                                            runAttention={runAttention}
                                            activeFilter={attentionFilter}
                                            onFilter={setAttentionFilter}
                                        />
                                        {attentionFilter && (
                                            <button
                                                onClick={() => setAttentionFilter(null)}
                                                className="bg-transparent border-none p-0 m-0 cursor-pointer text-xs text-accent-fg hover:underline"
                                            >
                                                Show all
                                            </button>
                                        )}
                                    </div>
                                    <button
                                        onClick={() => navigate('/operators-marketplace', { 
                                            state: { 
                                                projectId, 
                                                hideCustomize: true,
                                                projectName: project?.name || project?.projectName || 'Project'
                                            } 
                                        })}
                                        className="flex-none bg-accent text-on-accent px-4 py-2 rounded-lg font-bold text-sm hover:bg-accent/90 transition-all flex items-center gap-2 border-none cursor-pointer"
                                    >
                                        <AddIcon fontSize="small" />
                                        Add workflow
                                    </button>
                                </div>

                                {campaignsLoading ? (
                                    <p className="text-fg-2">Loading insights...</p>
                                ) : visibleGroups.length > 0 ? (
                                    <CatalogColumns minColumnWidth={CATALOG_MIN_COLUMN_WIDTH} spacing="roomy">
                                        {visibleGroups.map(([campaignType, groupCampaigns]) => (
                                            <CatalogGroup
                                                key={campaignType}
                                                label={campaignType}
                                                count={groupCampaigns.length}
                                                collapsed={isCollapsed(campaignType)}
                                                onToggle={() => toggleGroup(campaignType)}
                                            >
                                                {groupCampaigns.map((campaign) => (
                                                    <ProjectWorkflowCard
                                                        key={campaign.id}
                                                        campaign={campaign}
                                                        latestRun={runFor(campaign)}
                                                        onOpen={() => {
                                                            closeDrawers();
                                                            setDetailRuns(undefined);
                                                            setDetailCampaign(campaign);
                                                        }}
                                                        onOpenRuns={(runs) => {
                                                            closeDrawers();
                                                            setDetailRuns(runs);
                                                            setDetailCampaign(campaign);
                                                        }}
                                                        highlightBucket={attentionFilter}
                                                        onViewInsights={() => navigate(`/analytics/projects/${projectId}/campaign/${campaign.id}`, {
                                                            state: { campaign_name: campaign.campaign_name }
                                                        })}
                                                        onCustomize={() => handleCustomize(campaign)}
                                                        onViewBacktest={(run) => handleViewBacktest(campaign, run)}
                                                    />
                                                ))}
                                            </CatalogGroup>
                                        ))}
                                    </CatalogColumns>
                                ) : attentionFilter ? (
                                    <p className="text-fg-2 text-sm m-0">
                                        No workflows match this filter now.{' '}
                                        <button
                                            onClick={() => setAttentionFilter(null)}
                                            className="bg-transparent border-none p-0 m-0 cursor-pointer text-sm text-accent-fg hover:underline"
                                        >
                                            Show all
                                        </button>
                                    </p>
                                ) : (
                                    <div
                                        className="w-full py-12 text-center bg-surface/50 rounded-xl border border-dashed border-line cursor-pointer hover:border-accent-fg/40 transition-all group"
                                        onClick={() => navigate('/operators-marketplace', {
                                            state: {
                                                projectId,
                                                hideCustomize: true,
                                                projectName: project?.name || project?.projectName || 'Project'
                                            }
                                        })}
                                    >
                                        <p className="text-fg-2 m-0 mb-3">No members yet.</p>
                                        <span className="inline-flex items-center gap-2 text-accent-fg font-bold text-sm group-hover:underline">
                                            <AddIcon fontSize="small" />
                                            Add workflow
                                        </span>
                                    </div>
                                )}
                            </section>
                        )}
                    </div>
                </div>
            </main>

            {campaigns.map((campaign) => (
                <RunStatsReporter key={campaign.id} campaign={campaign} onRunStats={handleRunStats} />
            ))}

            {/* Campaign Detail Drawer */}
            {/* Docked from the insight drawer's width, so the assistant stays usable beside it. */}
            <EntityDetailDrawer
                open={Boolean(detailCampaign)}
                onClose={() => setDetailCampaign(null)}
                docked
                dockedMinWidth={1440}
                title={detailCampaign?.campaign_name ?? ''}
                // The description is folded below the runs instead (see the
                // children): the card already shows its opening lines, and a
                // chip that opens the drawer on failed runs must not land them
                // below a long description.
                description=""
                banner={detailCampaign && (
                    // Keyed on the runs too: the panel reads initialRuns on mount,
                    // so a second chip on the same card needs a fresh panel.
                    // The card's window, so a count on the card is the count
                    // the drawer opens on.
                    <WorkflowRunPanel
                        key={`${detailCampaign.id}:${detailRuns?.bucket ?? 'totals'}`}
                        campaignId={detailCampaign.id}
                        flowCode={detailCampaign.flow_template_code}
                        defaultWindow={ATTENTION_WINDOW}
                        initialRuns={detailRuns}
                        headerAction={
                            <button
                                onClick={() => {
                                    setDetailCampaign(null);
                                    navigate(`/analytics/projects/${projectId}/campaign/${detailCampaign.id}?tab=run-health`, {
                                        state: { campaign_name: detailCampaign.campaign_name }
                                    });
                                }}
                                className="bg-transparent border-none p-0 m-0 cursor-pointer text-xs font-mono text-accent-fg/70 hover:text-accent-fg transition-colors"
                            >
                                30-day trend →
                            </button>
                        }
                    />
                )}
                actions={detailCampaign && (
                    <>
                        <button
                            onClick={() => {
                                setDetailCampaign(null);
                                navigate(`/analytics/projects/${projectId}/campaign/${detailCampaign.id}`, {
                                    state: { campaign_name: detailCampaign.campaign_name }
                                });
                            }}
                            className="w-full font-bold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors border-none cursor-pointer"
                            style={{ backgroundColor: dsVar('accent'), color: dsVar('on-accent') }}
                        >
                            <RocketLaunchIcon fontSize="small" />
                            View Insights
                        </button>
                        {/* Same affordance as the card, for a reader who opened the
                            drawer instead of reading the card footer. */}
                        {detailDrawerRun && (
                            <button
                                onClick={() => {
                                    setDetailCampaign(null);
                                    handleViewBacktest(detailCampaign, detailDrawerRun);
                                }}
                                title={describeRun(detailDrawerRun)}
                                className="w-full bg-transparent border border-info/60 text-info hover:bg-info/10 font-bold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors cursor-pointer whitespace-nowrap overflow-hidden"
                            >
                                <QueryStatsIcon fontSize="small" />
                                View Backtest
                                {/* The run's date and size as a quiet aside, so the label stays one line. */}
                                {describeRunDetail(detailDrawerRun) && (
                                    <span className="font-normal text-sm opacity-80 truncate">· {describeRunDetail(detailDrawerRun)}</span>
                                )}
                            </button>
                        )}
                        <button
                            onClick={() => {
                                setDetailCampaign(null);
                                handleCustomize(detailCampaign);
                            }}
                            className="w-full bg-transparent border border-accent-fg text-accent-fg hover:bg-accent/10 font-bold py-2.5 px-4 rounded-lg flex items-center justify-center gap-2 transition-colors cursor-pointer"
                        >
                            <SettingsIcon fontSize="small" />
                            Customize
                        </button>
                    </>
                )}
            >
                {detailCampaign && (
                    <DrawerAboutSection key={detailCampaign.id} description={detailCampaign.campaign_description ?? ''} />
                )}
            </EntityDetailDrawer>

            {/*
              * No campaign is in view here: the insight names its own, which
              * the reasoning summary and its full view on that campaign's page
              * use. A review leaves nothing on this page to refresh.
              */}
            <InsightGroupDrawer
                campaignId={undefined}
                group={openInsight}
                onClose={() => setOpenInsight(null)}
                onReviewSubmitted={() => setBusinessRefresh((n) => n + 1)}
                showReasoningAction={(target) => projectId ? () => navigate(reasoningPathFor(projectId, target)) : undefined}
            />

            {/* The cases waiting at one review gate. */}
            <DecisionListDrawer
                gate={openGate}
                onClose={() => setOpenGate(null)}
                onOpenCard={(card, item) => {
                    closeDrawers();
                    setReviewCard({
                        campaignId: card.campaignId as string,
                        group: {
                            kind: 'group',
                            capsuleId: card.capsuleId as string,
                            groupIdentifier: card.groupIdentifier as string,
                            groupName: item.customerName || item.customerId
                        }
                    });
                }}
            />

            {/*
              * The card a waiting decision is made on. Its campaign is the one
              * that raised the review, not one this page has in view.
              */}
            <InsightGroupDrawer
                campaignId={reviewCard?.campaignId}
                group={reviewCard?.group ?? null}
                onClose={() => setReviewCard(null)}
                onReviewSubmitted={() => setBusinessRefresh((n) => n + 1)}
                showReasoningAction={(target) => projectId ? () => navigate(reasoningPathFor(projectId, target)) : undefined}
            />
        </div>
    );
};

export default ProjectReports;

